/**
 * Live RFIMS telemetry seen from the panels: which readings have a real
 * series behind them, the series fetch (telemetry/history), the per-metric
 * forecast limits (analysis LIVE_METRIC_CONFIG), the latest live limit
 * values (the live feed store) and the staleness of what the twin store
 * holds for the SIM chip.
 */
import { LIVE_METRIC_CONFIG, LIVE_METRIC_DEFAULT, type LiveMetricConfig } from '@/features/twin/analysis';
import { fetchLiveHistory, hasLiveHistory, liveHistorySeries } from '@/features/twin/telemetry/history';
import { isLiveSite } from '@/features/twin/telemetry/influxBridge';
import { latestLiveMetric } from '@/features/twin/telemetry/liveFeedStore';
import type { LiveSiteMetrics } from '@/features/twin/telemetry/types';
import type { Reading, ReadingSource } from '../../types';
import type { ReadingRef } from '../state';
import type { SeriesPoint } from '../lib/graphSeries';
import { parseDateMs } from '../lib/format';

export { isLiveSite };

/** The previous history popup asked for 90 days; the Worker aggregates per window so the payload stays small. */
export const LIVE_HISTORY_DAYS = 90;

/** True for a live site and one of the six readings InfluxDB keeps a series for. */
export function isLiveHistoryItem(assetId: string | null, ref: ReadingRef): boolean {
  return hasLiveHistory(assetId, ref.subsystemId, ref.dataId);
}

export function liveMetricFor(ref: ReadingRef): string | null {
  return liveHistorySeries(ref.subsystemId, ref.dataId)?.metric ?? null;
}

export function liveLimitsFor(ref: ReadingRef): LiveMetricConfig {
  return LIVE_METRIC_CONFIG[`${ref.subsystemId}|${ref.dataId}`] ?? LIVE_METRIC_DEFAULT;
}

/** Latest raw value of a feed metric (temp_max_c, humidity_min_pct, disk_total_mib, ...), null when the feed has none. */
export function latestMetric(metric: string): number | null {
  return latestLiveMetric(metric as keyof LiveSiteMetrics);
}

/** Chronological numeric series for a live reading; empty when the reading has no series; throws with the HTTP status on failure. */
export async function fetchLiveSeries(
  assetId: string,
  ref: ReadingRef,
  days: number = LIVE_HISTORY_DAYS,
): Promise<Array<SeriesPoint & { source: ReadingSource; raw: string }>> {
  const history = await fetchLiveHistory(assetId, ref.subsystemId, ref.dataId, days);
  if (!history) return [];
  const points: Array<SeriesPoint & { source: ReadingSource; raw: string }> = [];
  for (const p of history.points) {
    const t = parseDateMs(p.datetime);
    if (t !== null && Number.isFinite(p.numeric))
      points.push({ t, v: p.numeric, raw:p.value, source: history.source === 'influxdb' ? 'live' : 'sim' });
  }
  return points.sort((a, b) => a.t - b.t);
}

export interface Staleness {
  /** Newest reading time in the store, or null without readings. */
  latestMs: number | null;
  /** Age of that reading in ms (freshnessMs when the reading carries one). */
  ageMs: number | null;
  source: ReadingSource | null;
  count: number;
}

/** Age of the newest reading in the store (the SIM chip's staleness). */
export function stalenessOf(readings: Record<string, Reading>, nowMs: number): Staleness {
  let latest: Reading | null = null;
  let count = 0;
  for (const r of Object.values(readings)) {
    count++;
    if (!latest || r.timeMs > latest.timeMs) latest = r;
  }
  if (!latest) return { latestMs: null, ageMs: null, source: null, count };
  return {
    latestMs: latest.timeMs,
    ageMs: latest.freshnessMs ?? Math.max(0, nowMs - latest.timeMs),
    source: latest.source,
    count,
  };
}
